import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * A title field that wraps onto as many lines as the title needs instead of scrolling sideways.
 * It stays a single-line value: Enter finishes editing and pasted line breaks become spaces.
 * The height is measured in JavaScript because Firefox does not support `field-sizing: content`.
 */
export function TitleInput({
  value,
  onChange,
  onCommit,
  onCancel,
  className,
  ...props
}: Omit<React.ComponentProps<"textarea">, "onChange" | "value" | "rows"> & {
  value: string;
  onChange(value: string): void;
  /** Enter or leaving the field. */
  onCommit?(): void;
  /** Escape. */
  onCancel?(): void;
}) {
  const ref = React.useRef<HTMLTextAreaElement>(null);
  const fit = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  React.useLayoutEffect(fit, [value, fit]);
  React.useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    // A narrower or wider page changes where the title wraps.
    let width = el.clientWidth;
    let frame = 0;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === width) return;
      width = el.clientWidth;
      // After the observer's turn: resizing inside it would trigger another round of observations.
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    ro.observe(el);
    return () => {
      ro.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [fit]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\s*[\r\n]+\s*/g, " "))}
      onBlur={() => onCommit?.()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
        if (e.key === "Escape") onCancel?.();
      }}
      className={cn("block resize-none overflow-hidden break-words", className)}
      {...props}
    />
  );
}
